import { useAuthStore } from '~/stores/auth';
import type { APIErrorResponse, JsonApiResponse } from '~/interfaces/api/main';
import type { ApiResponse } from '~/types/main';
import { getAuthHeaders, normalizeApiError, normalizeJsonApiResponse } from '~/composables/http/utils';

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: unknown;
  query?: Record<string, string>;
}

export async function useApiFetch<T = unknown> (
  url: string,
  options?: ApiFetchOptions,
): Promise<ApiResponse<T>> {
  const authHeaders = await getAuthHeaders();
  const config = useRuntimeConfig();

  const fetchOptions = {
    baseURL: config.public.API_BASE_URL,
    ...options,
    headers: {
      ...authHeaders,
      ...options?.headers,
    },
  };

  let transactionId: string | null = null;

  try {
    const response = await $fetch.raw<JsonApiResponse<T>>(url, fetchOptions);
    transactionId = response.headers.get('X-Transaction-Id') ?? null;

    if (response.status === 204) {
      return { transactionId, errors: [], statusCode: 204 } as APIErrorResponse;
    }

    return normalizeJsonApiResponse<T>(response._data);
  } catch (error: any) {
    transactionId = error?.response?.headers?.get?.('X-Transaction-Id') ?? null;

    if (error?.response?.status === 401) {
      const authStore = useAuthStore();
      await authStore.fetchToken();
    }

    return normalizeApiError(
      error?.data,
      error?.response?.status ?? 500,
      transactionId,
      error?.data?.title,
    );
  }
}
